'use client';

import { useState, useEffect } from 'react';

const COOKIE_KEY = 'cookie_consent_accepted';

function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))
    ?.split('=')[1];
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const consent = getCookie(COOKIE_KEY);
    if (!consent) {
      // slight delay so it doesn't flash on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (accepted) => {
    setLeaving(true);
    setCookie(COOKIE_KEY, accepted ? 'accepted' : 'declined', 365);
    setTimeout(() => setVisible(false), 500);
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(110%); opacity: 0; }
        }
        .cookie-banner {
          animation: slideUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .cookie-banner.leaving {
          animation: slideDown 0.45s cubic-bezier(0.55, 0, 0.78, 0) forwards;
        }

        /* shimmer on the gold rule */
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        .gold-shimmer {
          background: linear-gradient(
            90deg,
            transparent 0%,
            #d4b896 30%,
            #f5deb3 50%,
            #d4b896 70%,
            transparent 100%
          );
          background-size: 200% auto;
          animation: shimmer 3s linear infinite;
        }
      `}</style>

      {/* Backdrop blur overlay (very subtle) */}
      <div
        className="fixed inset-0 z-[998] pointer-events-none"
        style={{ backdropFilter: 'blur(0px)' }}
      />

      {/* Banner */}
      <div
        className={`cookie-banner fixed bottom-0 left-0 right-0 z-[999] ${leaving ? 'leaving' : ''}`}
      >
        {/* Top shimmer rule */}
        <div className="gold-shimmer h-px w-full" />

        <div
          className="w-full px-4 md:px-8 py-5 md:py-6"
          style={{
            background: 'linear-gradient(135deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%)',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.45), 0 -1px 0 rgba(212,184,150,0.2)',
          }}
        >
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-5 md:gap-8">

            {/* Icon + text */}
            <div className="flex items-start gap-4 flex-1">
              {/* Cookie icon */}
              <div
                className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mt-0.5"
                style={{ background: 'rgba(191,135,93,0.15)', border: '1px solid rgba(191,135,93,0.35)' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#bf875d" strokeWidth="1.5" />
                  <circle cx="9"  cy="9"  r="1.2" fill="#bf875d" />
                  <circle cx="14" cy="8"  r="0.9" fill="#d4b896" />
                  <circle cx="15" cy="13" r="1.2" fill="#bf875d" />
                  <circle cx="10" cy="14" r="0.9" fill="#d4b896" />
                  <circle cx="12" cy="11" r="0.7" fill="#bf875d" />
                </svg>
              </div>

              <div>
                <p
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  className="text-lg md:text-xl font-light text-[#f0e6d6] leading-snug mb-1"
                >
                  We value your{' '}
                  <em className="italic text-[#bf875d]">privacy</em>
                </p>
                <p
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-[11px] font-light leading-relaxed tracking-wide"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    color: 'rgba(212,184,150,0.75)',
                    fontSize: '11px',
                    lineHeight: '1.7',
                    letterSpacing: '0.02em',
                  }}
                >
                  We use cookies to enhance your browsing experience, personalise content and
                  analyse site traffic. By clicking{' '}
                  <span className="text-[#d4b896]">Accept</span>, you consent to our use of
                  cookies.{' '}
                  <a
                    href="/privacy-policy"
                    className="underline underline-offset-2 hover:text-[#bf875d] transition-colors duration-200"
                    style={{ color: 'rgba(212,184,150,0.9)' }}
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-shrink-0 pl-14 md:pl-0">
              {/* Decline */}
              <button
                onClick={() => dismiss(false)}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-[10px] font-medium tracking-[0.2em] uppercase px-4 py-2.5 rounded-full transition-all duration-300"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '10px',
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  padding: '10px 18px',
                  borderRadius: '999px',
                  border: '1px solid rgba(212,184,150,0.25)',
                  color: 'rgba(212,184,150,0.6)',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(212,184,150,0.5)';
                  e.currentTarget.style.color = 'rgba(212,184,150,0.9)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'rgba(212,184,150,0.25)';
                  e.currentTarget.style.color = 'rgba(212,184,150,0.6)';
                }}
              >
                Decline
              </button>

              {/* Accept */}
              <button
                onClick={() => dismiss(true)}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  padding: '10px 22px',
                  borderRadius: '999px',
                  border: '1px solid #bf875d',
                  color: '#1a1208',
                  background: 'linear-gradient(135deg, #d4a96a 0%, #bf875d 50%, #a8723d 100%)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(191,135,93,0.3)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #e0b87a 0%, #cf9560 50%, #b8824d 100%)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(191,135,93,0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #d4a96a 0%, #bf875d 50%, #a8723d 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(191,135,93,0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Accept All
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}